/**
 * 键盘快捷键工具类
 * 自动处理 macOS 和 Windows/Linux 平台的按键差异
 */

class KeyboardShortcuts {
  constructor() {
    // 检测操作系统
    this.isMac = /Mac|iPhone|iPod|iPad/.test(navigator.platform)
    this.listeners = new Map()
    this.isEnabled = true
  }

  /**
   * 获取修饰键名称(用于显示)
   */
  getModifierKeyName() {
    return this.isMac ? '⌘' : 'Ctrl'
  }

  /**
   * 检查是否按下了正确的修饰键
   * @param {KeyboardEvent} event
   */
  isModifierKeyPressed(event) {
    return this.isMac ? event.metaKey : event.ctrlKey
  }

  /**
   * 注册快捷键
   * @param {String} key - 按键(例如: 'k', 'f')
   * @param {Function} callback - 回调函数
   * @param {Object} options - 配置选项
   * @param {Boolean} options.preventDefault - 是否阻止默认行为(默认: true)
   * @param {Boolean} options.requireModifier - 是否需要修饰键(默认: true)
   * @param {Boolean} options.allowInInput - 是否允许在输入框中触发(默认: false)
   */
  register(key, callback, options = {}) {
    const {
      preventDefault = true,
      requireModifier = true,
      allowInInput = false
    } = options

    const normalizedKey = typeof key === 'string' ? key.toLowerCase() : ''
    if (!normalizedKey) {
      return () => {}
    }

    const handler = (event) => {
      if (!this.isEnabled) return

      const eventKey = typeof event.key === 'string' ? event.key.toLowerCase() : ''

      // 检查是否按下了目标按键
      if (!eventKey || eventKey !== normalizedKey) return

      // 检查修饰键(如果需要)
      if (requireModifier && !this.isModifierKeyPressed(event)) return

      // 如果不需要修饰键,但按下了修饰键,则不触发
      if (!requireModifier && (event.ctrlKey || event.metaKey)) return

      // 检查是否在输入框中
      if (!allowInInput) {
        const target = event.target
        const tagName = target && target.tagName ? target.tagName.toLowerCase() : ''
        const isInput = tagName === 'input' || tagName === 'textarea' || !!(target && target.isContentEditable)

        // 如果不允许在输入框中触发,则返回
        if (isInput) return
      }

      if (preventDefault) {
        event.preventDefault()
      }

      callback(event)
    }

    // 保存监听器引用以便后续移除
    this.listeners.set(key, handler)
    document.addEventListener('keydown', handler)

    return () => this.unregister(key)
  }

  /**
   * 注销快捷键
   * @param {String} key
   */
  unregister(key) {
    const handler = this.listeners.get(key)
    if (handler) {
      document.removeEventListener('keydown', handler)
      this.listeners.delete(key)
    }
  }

  /**
   * 注销所有快捷键
   */
  unregisterAll() {
    this.listeners.forEach((handler) => {
      document.removeEventListener('keydown', handler)
    })
    this.listeners.clear()
  }

  /**
   * 启用/禁用快捷键
   */
  setEnabled(enabled) {
    this.isEnabled = enabled
  }

  /**
   * 格式化快捷键显示文本
   * @param {String} key - 按键
   * @param {Boolean} requireModifier - 是否需要修饰键
   */
  formatShortcut(key, requireModifier = true) {
    if (requireModifier) {
      return `${this.getModifierKeyName()}+${key.toUpperCase()}`
    }
    return key.toUpperCase()
  }
}

// 导出单例
export default new KeyboardShortcuts()
